<template>
  <div class="flex flex-col gap-2 w-full">
    <div class="flex gap-2 w-full items-start">
      <div class="relative flex-1">
        <VInput
          v-model="currentSearchTerm"
          type="text"
          placeholder="Invite users via email"
          :disabled="loading"
          class="w-full"
          @click="handleInputClick"
          @blur="handleInputBlur"
        />
        <div
          v-if="isSearchOpen"
          class="absolute z-10 w-full mt-1 bg-white border border-gray-600 rounded-md shadow-sm max-h-96 overflow-y-auto"
        >
          <button
            v-for="user in searchResults"
            :key="user.id"
            class="flex items-center gap-2 px-4 py-2 text-sm cursor-pointer w-full text-left hover_bg-gray-100"
            @click="selectUser(user)"
          >
            <div class="flex flex-col">
              <span>{{ user.name }}</span>
              <span v-if="user.email_address" class="text-gray-500 text-xs">{{ user.email_address }}</span>
            </div>
          </button>
        </div>
      </div>
      <VButton
        size="md"
        :disabled="loading"
        :aria-label="loading ? 'Inviting users...' : 'Invite selected users'"
        @click="handleInviteUser"
      >
        Invite
      </VButton>
    </div>

    <!-- Inline notification -->
    <div
      v-if="notification.show"
      :class="[
        'px-4 py-2 rounded-md text-sm',
        notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : '',
        notification.type === 'danger' ? 'bg-red-100 text-red-800 border border-red-200' : '',
        notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : '',
      ]"
    >
      <div class="font-medium">{{ notification.title }}</div>
      <div v-if="notification.text">{{ notification.text }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from '~~/lib/debounce'
import type { User as APIUser } from '~~/entities/user'
import { DeckPermissionsUserRole } from '~~/entities/deck'
import * as Sentry from '@sentry/vue'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'

const loading = defineModel<boolean>('loading', { required: true })

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
  existingUsers?: { user_id: number; email?: string }[]
}>()

const emit = defineEmits<{
  userInvited: []
}>()

const { upsertDeckUserPermissions } = useDeckPermissions()
const { $analytics } = useNuxtApp()

const searchResults = ref<APIUser[]>([])
const currentSearchTerm = ref('')
const selectedUser = ref<APIUser | null>(null)
const notification = ref({
  show: false,
  type: '' as 'success' | 'danger' | 'warning',
  title: '',
  text: '',
})

const showDropdown = ref(false)
const isSearchOpen = computed(() => searchResults.value.length > 0 && !loading.value && showDropdown.value)

function selectUser(user: APIUser) {
  selectedUser.value = user
  currentSearchTerm.value = user.email_address || user.name || ''
  closeSearch()
}

function showNotification(type: 'success' | 'danger' | 'warning', title: string, text: string = '') {
  notification.value = { show: true, type, title, text }
  setTimeout(() => {
    notification.value.show = false
  }, 5000)
}

async function handleInviteUser() {
  $analytics.track('deckShareInviteUser')

  if (!selectedUser.value) {
    showNotification('danger', 'No User Selected', 'Please select a user from the search results.')
    return
  }

  // Double-check if user is already in the list
  if (isUserAlreadyInvited(selectedUser.value)) {
    showNotification('warning', 'User Already Invited', `${selectedUser.value.name} is already in the presentation permissions.`)
    return
  }

  try {
    loading.value = true

    await upsertDeckUserPermissions({
      deckId: props.deck.id,
      companyId: props.deck.company_id,
      user_id: selectedUser.value.id,
      role: DeckPermissionsUserRole.Viewer,
    })

    showNotification('success', 'User Invited', `${selectedUser.value.name} has been invited to the presentation`)

    // Clear after invite
    currentSearchTerm.value = ''
    selectedUser.value = null

    // Emit event to parent to refetch permissions
    emit('userInvited')
  } catch (error) {
    showNotification('danger', 'Invitation Failed', 'Failed to invite user. Please try again.')
    Sentry.captureException(error)
  } finally {
    loading.value = false
  }
}

function isUserAlreadyInvited(user: APIUser): boolean {
  if (!props.existingUsers) return false

  return props.existingUsers.some(
    existingUser =>
      existingUser.user_id === user.id || (existingUser.email && user.email_address && existingUser.email === user.email_address),
  )
}

async function performUserSearch() {
  const term = currentSearchTerm.value.trim()
  if (!term) {
    if (showDropdown.value) {
      await loadInitialUsers()
    } else {
      closeSearch()
    }
    return
  }

  try {
    const response = await $fetch<{ results: APIUser[] }>(`/v2/companies/${props.deck.company_id}/admins`, {
      ...useFetchSharedOptions(),
      query: { query: term },
    })
    const filteredResults = filterExistingUsers(response.results || [])
    searchResults.value = filteredResults

    // Auto-select user if email matches exactly
    const exactEmailMatch = filteredResults.find(user => user.email_address && user.email_address.toLowerCase() === term.toLowerCase())
    if (exactEmailMatch) {
      selectedUser.value = exactEmailMatch
    } else {
      selectedUser.value = null
    }
  } catch (error) {
    searchResults.value = []
    selectedUser.value = null
    Sentry.captureException(error)
  }
}

function closeSearch() {
  searchResults.value = []
  showDropdown.value = false
}

async function handleInputClick() {
  showDropdown.value = true
  if (currentSearchTerm.value.trim()) {
    await performUserSearch()
  } else {
    // Load initial users when clicking with no search term
    await loadInitialUsers()
  }
}

function handleInputBlur() {
  // Use setTimeout to allow click events on dropdown items to fire first
  setTimeout(() => {
    closeSearch()
  }, 150)
}

async function loadInitialUsers() {
  try {
    const response = await $fetch<{ results: APIUser[] }>(`/v2/companies/${props.deck.company_id}/admins`, {
      ...useFetchSharedOptions(),
    })
    searchResults.value = filterExistingUsers(response.results || [])
  } catch (error) {
    searchResults.value = []
    Sentry.captureException(error)
  }
}

function filterExistingUsers(users: APIUser[]): APIUser[] {
  if (!props.existingUsers) return users

  const existingUserIds = new Set(props.existingUsers.map(u => u.user_id))
  const existingEmails = new Set(props.existingUsers.map(u => u.email).filter(Boolean))

  return users.filter(user => {
    return !existingUserIds.has(user.id) && (!user.email_address || !existingEmails.has(user.email_address))
  })
}

watch(currentSearchTerm, debounce(performUserSearch, 250))
</script>
